import type { UseQueryResult } from '@tanstack/react-query';
import type { Dictionary } from 'lodash';
import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import type { AssistQuery } from 'services/assist-api/query';
import { useQueryAssist } from 'services/assist-api/query-client';

export type NationalNorm = {
  id: number;
  activeFrom: string;
  activeTo: string | null;
  area: {
    id: number;
  };
  level: {
    id: number;
  };
  std: number;
  mean: number;
  minimum: number;
  median: number;
  maximum: number;
  lowerQuartile: number;
  upperQuartile: number;
};

export type BaseReferenceDataNationalNorm = API_JSON & {
  activeFrom: string;
  activeTo: string | null;
  scaleStd: number;
  scaleMean: number;
  scaleMinimum: number;
  scaleMedian: number;
  scaleMaximum: number;
  scaleLowerQuartile: number;
  scaleUpperQuartile: number;
  area: API_JSON;
  level: API_JSON;
};

const order = { 'order[order]': 'asc' } as const;
const groups = [];
/**
 * Fetch reference-data-national-norms
 * @param params can be used to customise/limit the reference-data-national-norms retrieved
 * @returns an array of reference-data-national-norms
 */
export const getReferenceDataNationalNorms: GetMany<BaseReferenceDataNationalNorm> = async (
  params
) =>
  (
    await ASSIST_API.get('reference-data-national-norms', {
      params: requestParams({ order, groups }, params)
    })
  ).data;

/**
 * Fetch reference-data-national-norm (singular)
 * @param params can be used to customise/limit the reference-data-national-norms retrieved
 * @returns a single reference-data-national-norm
 */
export const getReferenceDataNationalNorm: GetOne<BaseReferenceDataNationalNorm> = async (
  id,
  params
) =>
  (
    await ASSIST_API.get(`reference-data-national-norms/${id}`, {
      params: requestParams({ order, groups }, params)
    })
  ).data;

/**
 * Hook implementation to fetch reference-data-national-norms
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns reference-data-national-norms matching request
 */
export const useReferenceDataNationalNorms: AssistQuery.UseMany<BaseReferenceDataNationalNorm> = (
  params,
  options
) =>
  useQueryAssist(
    ['reference-data-national-norms', params],
    () => getReferenceDataNationalNorms(params),
    {
      staleTime: STALE_LONG,
      ...options
    }
  );

/**
 * Given an reference-data-national-normId fetch and return the reference-data-national-norm
 *  - only runs once reference-data-national-normId is provided
 *  - fetches the one reference-data-national-norm
 *  - caches data
 * @param id reference-data-national-normId
 * @param params optional parameters to change requested data
 * @returns the matching reference-data-national-norm
 */
export const useReferenceDataNationalNorm: AssistQuery.UseOne<BaseReferenceDataNationalNorm> = (
  id,
  params,
  options
) =>
  useQueryAssist(
    ['reference-data-national-norm', { id, ...params }],
    () => getReferenceDataNationalNorm(id, params),
    { staleTime: STALE_LONG, ...options, enabled: !!id }
  );

export type ReferenceDataNationalNorm = BaseReferenceDataNationalNorm & {
  std: number;
  mean: number;
  minimum: number;
  median: number;
  maximum: number;
  lowerQuartile: number;
  upperQuartile: number;
};

export type ReferenceDataNationalNormDictionary = Dictionary<ReferenceDataNationalNorm>;

/**
 * Given an area and level ids fetch and return the reference-data-national-norms with NationalNorm format
 *  - fetches the one reference-data-national-norm
 *  - caches data
 * @param area
 * @param level
 * @returns the matching reference-data-national-norm with NationalNorm format
 */
export const useReferenceDataNationalNormsByAreaAndLevels = (
  area,
  levels?
): UseQueryResult<RequestResponse<BaseReferenceDataNationalNorm[]>> =>
  useQueryAssist(
    ['reference-data-national-norms', { filters: { area, level: levels } }],
    () =>
      getReferenceDataNationalNorms({
        filters: { area, level: levels }
      }),
    {
      staleTime: STALE_LONG,
      enabled: !!area
    }
  );
